import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { CopyableCode } from '../../components/ui/CopyableCode';
import { Fold } from '../../components/ui/Fold';
import * as scanner from '../../platform/scanner';
import { useAuth } from '../auth/AuthContext';
import { LocationLink } from '../locations/LocationLink';
import { LocationPicker } from '../locations/LocationPicker';
import { PrintDialog } from '../labels/PrintDialog';
import { MoveUnits } from './MoveUnits';
import { SerialScanner } from './SerialScanner';
import {
  listSerials,
  registerSerials,
  serialHistory,
  serialSummary,
  updateSerial,
  type SerialStatus,
  type SerialUnit,
} from './api';

const STATUSES: SerialStatus[] = ['in_stock', 'reserved', 'issued', 'scrapped', 'missing'];

/** Units that are physically on a shelf, and so can be taken off one. Reserved
 *  counts: it is spoken for, not gone. */
const ON_A_SHELF: SerialStatus[] = ['in_stock', 'reserved'];

function History({ unitId }: { unitId: string }) {
  const { t } = useTranslation();
  const rows = useQuery({
    queryKey: ['serials', 'history', unitId],
    queryFn: () => serialHistory(unitId),
  });

  if (!rows.data?.length) return <p className="serials__empty">{t('serials.noHistory')}</p>;

  return (
    <ol className="serials__history">
      {rows.data.map((row) => (
        <li key={row.movement_id}>
          <span className="serials__when">{new Date(row.occurred_at).toLocaleString()}</span>
          <span>{t(`stock.types.${row.movement_type}`, row.movement_type)}</span>
          <span>{row.location_name ?? t('serials.outside')}</span>
          {row.note && <span className="serials__note">{row.note}</span>}
        </li>
      ))}
    </ol>
  );
}

function Row({
  unit,
  chosen,
  onChoose,
  showChoice,
  onPrint,
  focused,
}: {
  unit: SerialUnit;
  chosen: boolean;
  /** Null when this unit cannot be moved — issued, scrapped, or no permission. */
  onChoose: (() => void) | null;
  showChoice: boolean;
  onPrint: (unit: SerialUnit) => void;
  focused: boolean;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [serial, setSerial] = useState(unit.serial ?? '');
  const [note, setNote] = useState(unit.note ?? '');

  useEffect(() => { if (focused) setOpen(true); }, [focused]);

  const save = useMutation({
    mutationFn: () => updateSerial(unit.id, { serial: serial.trim() || null, note }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['serials'] }),
  });

  return (
    <>
      <tr>
        {showChoice && (
          <td>
            <input
              type="checkbox"
              checked={chosen}
              disabled={!onChoose}
              onChange={() => onChoose?.()}
              aria-label={unit.serial ?? unit.code}
            />
          </td>
        )}
        <td>
          <button type="button" className="link" onClick={() => setOpen((value) => !value)}>
            {unit.code}
          </button>
        </td>
        <td>
          {/* A serial number exists to be carried elsewhere — into a warranty
              claim, a message, a search box — and it is exactly the kind of
              string a mouse selection clips a character off. */}
          {unit.serial ? (
            <CopyableCode value={unit.serial} label={t('serials.factorySerial')} />
          ) : (
            <span className="serials__own">{t('serials.ownNumber')}</span>
          )}
        </td>
        <td>
          <span className={`badge badge--${unit.status}`}>{t(`serials.status.${unit.status}`)}</span>
        </td>
        <td>
          {/* The name alone identifies nothing — "3 полка" exists in every
              cabinet — so the chain comes with it on hover. */}
          <LocationLink locationId={unit.current_location_id} name={unit.location_name} />
        </td>
      </tr>

      {open && (
        <tr className="admin__detail-row">
          <td colSpan={showChoice ? 5 : 4}>
            <div className="serials__detail">
              {can('label.print') && <button type="button" onClick={() => onPrint(unit)}>{t('labels.print')}</button>}
              {can('stock.post') && (
                <div className="serials__edit">
                  <label htmlFor={`sn-${unit.id}`}>{t('serials.factorySerial')}</label>
                  <input
                    id={`sn-${unit.id}`}
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                  />
                  <label htmlFor={`note-${unit.id}`}>{t('common.note')}</label>
                  {/* A unit's note is where "case scratched, works" and "RMA
                      2026-03, board replaced" go, and both want a line of their
                      own. */}
                  <textarea
                    id={`note-${unit.id}`}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                  />
                  <button type="button" onClick={() => save.mutate()}>
                    {t('common.save')}
                  </button>
                  {/* Where it is and what state it is in are the ledger's to
                      say — editing them here would let the register and the
                      ledger disagree, and the ledger is the one that wins. */}
                  <p className="admin__hint">{t('serials.placeIsLedger')}</p>
                </div>
              )}
              <History unitId={unit.id} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Register({ componentId }: { componentId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [text, setText] = useState('');
  const [count, setCount] = useState('');
  const [locationId, setLocationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const cameraAvailable = scanner.cameraSupport() === 'ok';

  const add = useMutation({
    mutationFn: () => {
      // One per line: a packing list is copied out of a text file or typed off
      // the boxes, and both of those arrive as lines.
      const serials = text
        .split(/[\n,;]+/)
        .map((value) => value.trim())
        .filter(Boolean);

      return registerSerials({
        component_id: componentId,
        ...(serials.length ? { serials } : { count: Number(count) || 0 }),
        location_id: locationId,
      });
    },
    onSuccess: () => {
      setText('');
      setCount('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['serials'] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.code : 'unknown'),
  });

  return (
    <details className="serials__register">
      <summary>{t('serials.register')}</summary>
      <div className="serials__register-body">
        <p className="admin__hint">{t('serials.registerHint')}</p>

      <div className="serials__register-form">
        <div className="field field--wide">
          <label htmlFor="serials-text">{t('serials.factorySerials')}</label>
          <textarea
            id="serials-text"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('serials.serialsPlaceholder')}
          />
          {cameraAvailable && (
            <button type="button" className="btn" onClick={() => setScanning(true)}>
              {t('serials.scan.open')}
            </button>
          )}
        </div>

        <div className="serials__register-side">
          {/* The two ways of saying how many are alternatives, so the second is
              switched off the moment the first is used. */}
          <div className="field">
            <label htmlFor="serials-count">{t('serials.ownCount')}</label>
            <input
              id="serials-count"
              inputMode="numeric"
              value={count}
              disabled={text.trim().length > 0}
              onChange={(e) => setCount(e.target.value)}
            />
          </div>

          {/* No "outside the warehouse": registering a unit is not a movement,
              and a place given here only says where it already is. */}
          <div className="field">
            <label>{t('serials.placeNow')}</label>
            <LocationPicker
              label={t('serials.placeNow')}
              value={locationId}
              onChange={setLocationId}
              allowOutside={false}
              componentId={componentId}
            />
          </div>

          <button
            type="button"
            className="btn btn--primary"
            onClick={() => add.mutate()}
            disabled={add.isPending || (!text.trim() && !count)}
          >
            {t('common.add')}
          </button>
        </div>
      </div>

      {scanning && (
        <SerialScanner
          componentId={componentId}
          initial={text.split('\n').map((value) => value.trim()).filter(Boolean)}
          onCancel={() => setScanning(false)}
          onDone={(scanned) => {
            setText(scanned.join('\n'));
            setScanning(false);
          }}
        />
      )}

      {error && (
        <p className="security__warning" role="alert">
          {t(`serials.errors.${error}`, { defaultValue: t('serials.errors.unknown') })}
        </p>
      )}
      </div>
    </details>
  );
}

/**
 * The register of individual units, shown only for components that are tracked
 * that way. For everything else it is absent rather than empty: a panel saying
 * "no units" on a reel of resistors is noise on every card in the catalogue.
 */
export function SerialsPanel({ componentId, focusId }: { componentId: string; focusId?: string | null }) {
  const { t } = useTranslation();
  const { can } = useAuth();

  const [status, setStatus] = useState<SerialStatus | ''>('');
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [printing, setPrinting] = useState<SerialUnit | null>(null);

  const summary = useQuery({
    queryKey: ['serials', 'summary', componentId],
    queryFn: () => serialSummary(componentId),
  });
  const units = useQuery({
    queryKey: ['serials', 'list', componentId, status, query],
    queryFn: () => listSerials(componentId, status || undefined, query || undefined),
  });

  const canMove = can('stock.post');
  // Only what is actually on a shelf. An issued or scrapped unit has no place
  // to be taken off, and offering to move it would be offering a lie.
  const movable = canMove
    ? (units.data ?? []).filter((unit) => ON_A_SHELF.includes(unit.status))
    : [];
  const allChosen = movable.length > 0 && movable.every((unit) => chosen.has(unit.id));

  // The shelves these units are actually on, in the order they first appear —
  // which is the order of the table, so the chips and the rows agree.
  const byPlace: { id: string; name: string; units: SerialUnit[] }[] = [];
  for (const unit of movable) {
    if (!unit.current_location_id) continue;
    const found = byPlace.find((place) => place.id === unit.current_location_id);
    if (found) found.units.push(unit);
    else
      byPlace.push({
        id: unit.current_location_id,
        name: unit.location_name ?? unit.current_location_id,
        units: [unit],
      });
  }

  return (
    <>
      <Fold
      title={t('serials.title')}
      count={summary.data?.total ?? null}
      className="panel__section serials"
    >
      <div className="serials__summary">
        <span>{t('serials.total', { count: summary.data?.total ?? 0 })}</span>
        {STATUSES.map((value) => {
          const number = summary.data?.by_status[value] ?? 0;
          return number > 0 ? (
            <span key={value} className={`badge badge--${value}`}>
              {t(`serials.status.${value}`)}: {number}
            </span>
          ) : null;
        })}
      </div>

      <div className="serials__filters">
        <select value={status} onChange={(e) => setStatus(e.target.value as SerialStatus | '')}>
          <option value="">{t('serials.anyStatus')}</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`serials.status.${value}`)}
            </option>
          ))}
        </select>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('serials.search')}
        />
      </div>

      {units.data?.length ? (
        <>
          {movable.length > 0 && (
            <div className="serials__bulk">
              <div className="serials__pick">
                <label className="components__instock">
                  <input
                    type="checkbox"
                    checked={allChosen}
                    // Some but not all: the box says so rather than pretending
                    // to be one of the two states it is not in.
                    ref={(node) => {
                      if (node) node.indeterminate = chosen.size > 0 && !allChosen;
                    }}
                    onChange={() =>
                      setChosen(allChosen ? new Set() : new Set(movable.map((unit) => unit.id)))
                    }
                  />
                  {t('serials.move.all')}
                </label>

                {/* By shelf, which is how the job actually arrives: a cabinet is
                    emptied, not "every unit of this part everywhere". One press
                    per place beats ticking eleven boxes and missing one. */}
                {byPlace.length > 1 &&
                  byPlace.map((place) => {
                    const ids = place.units.map((unit) => unit.id);
                    const whole = ids.every((id) => chosen.has(id));
                    return (
                      <button
                        key={place.id}
                        type="button"
                        className={whole ? 'badge badge--link is-chosen' : 'badge badge--link'}
                        onClick={() =>
                          setChosen((current) => {
                            const next = new Set(current);
                            for (const id of ids) {
                              if (whole) next.delete(id);
                              else next.add(id);
                            }
                            return next;
                          })
                        }
                      >
                        {place.name} {place.units.length}
                      </button>
                    );
                  })}
              </div>
              {chosen.size > 0 && (
                <MoveUnits
                  componentId={componentId}
                  chosen={movable.filter((unit) => chosen.has(unit.id))}
                  onDone={() => setChosen(new Set())}
                  onCancel={() => setChosen(new Set())}
                />
              )}
            </div>
          )}

          <div className="scroll-x">
            <table className="serials__table">
              <thead>
                <tr>
                  {canMove && <th aria-label={t('serials.move.choose')} />}
                  <th>{t('serials.code')}</th>
                  <th>{t('serials.factorySerial')}</th>
                  <th>{t('serials.statusColumn')}</th>
                  <th>{t('serials.where')}</th>
                </tr>
              </thead>
              <tbody>
                {units.data.map((unit) => (
                  <Row
                    key={unit.id}
                    unit={unit}
                    chosen={chosen.has(unit.id)}
                    onChoose={
                      canMove && ON_A_SHELF.includes(unit.status)
                        ? () =>
                            setChosen((current) => {
                              const next = new Set(current);
                              if (next.has(unit.id)) next.delete(unit.id);
                              else next.add(unit.id);
                              return next;
                            })
                        : null
                    }
                    showChoice={canMove}
                    onPrint={setPrinting}
                    focused={unit.id === focusId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="serials__empty">{t('serials.none')}</p>
      )}

      {printing && <PrintDialog entityKind="serial" entityId={printing.id} onClose={() => setPrinting(null)} />}
      {can('stock.post') && <Register componentId={componentId} />}
      </Fold>
    </>
  );
}
